import React, { useEffect, useState } from "react";
import { errorMessage } from "../../services";
import { getAppSettings, saveAppSettings } from "../../services/admin";

const PLAN_OPTIONS = [
  { value: "trial", label: "Free trial" },
  { value: "monthly", label: "Monthly" },
  { value: "yearly", label: "Yearly" },
];

// How sellers and partners reach Ownezzy: the WhatsApp help button and links in the admin panel, and ownezzy.com's
// help button and legal pages. Saved with its own button, as it is typed.
function OwnezzyContact({ settings, disabled, save }) {
  const [form, setForm] = useState({ supportEmail: settings.supportEmail, supportWhatsapp: settings.supportWhatsapp });
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const changed = form.supportEmail.trim() !== settings.supportEmail || form.supportWhatsapp.replace(/\D/g, "") !== settings.supportWhatsapp;
  const submit = (e) => {
    e.preventDefault();
    save({ supportEmail: form.supportEmail.trim(), supportWhatsapp: form.supportWhatsapp.trim() });
  };
  return (
    <form className="config-row config-contact" onSubmit={submit}>
      <div>
        <b>Ownezzy contact</b>
        <small>How sellers and partners reach Ownezzy: the WhatsApp help button and links in the admin panel, and the help button and legal pages on ownezzy.com.</small>
        <div className="config-contact-fields">
          <label className="admin-field">Email<input type="email" required maxLength={120} value={form.supportEmail} onChange={set("supportEmail")} disabled={disabled} /></label>
          <label className="admin-field">WhatsApp number<input required inputMode="tel" maxLength={20} value={form.supportWhatsapp} onChange={set("supportWhatsapp")} disabled={disabled} placeholder="91 88937 25078" /></label>
        </div>
      </div>
      <button className="primary-btn" disabled={disabled || !changed}>Save contact</button>
    </form>
  );
}

// Settings -> Configuration: the free trial shown or hidden (sign-up and ownezzy.com plans), the plan already chosen
// when "Choose your plan" opens, the store owner tutorial and Ownezzy's contact. Each switch saves at once.
export default function ConfigurationSettings({ notify }) {
  const [settings, setSettings] = useState(null); // null while loading
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getAppSettings().then(({ data }) => setSettings(data.settings)).catch((err) => notify(errorMessage(err), "error"));
  }, [notify]);

  const save = async (changes) => {
    setBusy(true);
    try {
      const { data } = await saveAppSettings(changes);
      setSettings(data.settings);
      notify(data.message);
    } catch (err) {
      notify(errorMessage(err), "error");
    } finally {
      setBusy(false);
    }
  };

  // Hiding the trial while it is the default plan moves the default to Monthly in the same save
  const toggleTrial = (e) => {
    const on = e.target.checked;
    save(!on && settings.defaultPlan === "trial" ? { freeTrialEnabled: false, defaultPlan: "monthly" } : { freeTrialEnabled: on });
  };

  const off = settings === null || busy;
  return (
    <div className="panel info-panel tab-gap config-panel">
      <h2>Configuration</h2>
      <p>How sellers sign up on the demo stores and ownezzy.com, and what new store owners see first. Changes apply at once.</p>
      {settings && (
        <>
          <div className="config-row">
            <div>
              <b>Free trial</b>
              <small>Shows the free trial on the sign-up and the plans on ownezzy.com. Hidden: sellers sign up on a paid plan only.</small>
            </div>
            <label className="config-switch">
              <input type="checkbox" checked={settings.freeTrialEnabled} disabled={off} onChange={toggleTrial} />
              <span>{settings.freeTrialEnabled ? "Shown" : "Hidden"}</span>
            </label>
          </div>
          <div className="config-row">
            <div>
              <b>Plan selected by default</b>
              <small>The plan already chosen when "Choose your plan" opens on the sign-up.</small>
            </div>
            <select value={settings.defaultPlan} disabled={off} onChange={(e) => save({ defaultPlan: e.target.value })} aria-label="Plan selected by default">
              {PLAN_OPTIONS.filter((p) => settings.freeTrialEnabled || p.value !== "trial").map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>
          <div className="config-row">
            <div>
              <b>Store owner tutorial</b>
              <small>A short guided tour of the admin panel every store owner finishes on their first sign-in.</small>
            </div>
            <label className="config-switch">
              <input type="checkbox" checked={settings.tutorialEnabled} disabled={off} onChange={(e) => save({ tutorialEnabled: e.target.checked })} />
              <span>{settings.tutorialEnabled ? "On" : "Off"}</span>
            </label>
          </div>
          {/* Keyed by the saved values, so the fields show what was saved (e.g. a 10-digit number gets 91) */}
          <OwnezzyContact key={`${settings.supportEmail}|${settings.supportWhatsapp}`} settings={settings} disabled={off} save={save} />
        </>
      )}
    </div>
  );
}
